import { useEffect, useRef } from "react";

/**
 * Tracks the bounding rect of an element via a stable ref. Refreshes on:
 *   - mount (initial measurement)
 *   - window resize
 *   - element resize (ResizeObserver) — catches cases like minimize/expand
 *     transitions where the surrounding layout changes but the window doesn't.
 *
 * Caller passes a getter so the hook works with both regular HTMLElement refs
 * and Konva-style indirection (`stageRef.current?.container()`). Memoize the
 * getter with `useCallback` to avoid re-running the effect each render.
 */
export const useTrackedRect = (getElement: () => Element | null): React.MutableRefObject<DOMRect | null> => {
  const rectRef = useRef<DOMRect | null>(null);

  useEffect(() => {
    const update = () => {
      const el = getElement();
      if (el) rectRef.current = el.getBoundingClientRect();
    };
    update();

    const el = getElement();
    let observer: ResizeObserver | undefined;
    if (el && typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(update);
      observer.observe(el);
    }
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
      observer?.disconnect();
    };
  }, [getElement]);

  return rectRef;
};
